/**
 * PDF Export Utility - Export professionale con jsPDF e jspdf-autotable
 */
import jsPDF from 'jspdf';
import autoTable, { RowInput, CellInput } from 'jspdf-autotable';
import { ExportColumn, ExportConfig, SummaryRow, MultiSheetExportConfig } from './exportTypes';
import {
  EXPORT_CONFIG,
  generateFilename,
  formatDateForExport,
  formatCurrencyForExport,
  formatBooleanForExport,
  formatNumberForExport,
  formatPercentageForExport,
} from './exportConfig';

/**
 * Ottiene il valore di un campo nested (es: "risorsa.nome")
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((current, key) => {
    return current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined;
  }, obj as unknown);
}

/**
 * Formatta il valore in base al tipo di colonna per PDF
 */
function formatCellValueForPdf(value: unknown, type: ExportColumn['type']): string {
  if (value === null || value === undefined) return '';

  switch (type) {
    case 'date':
      return formatDateForExport(value as Date | string);
    case 'currency':
      return formatCurrencyForExport(typeof value === 'number' ? value : parseFloat(String(value)) || 0);
    case 'number':
      return formatNumberForExport(typeof value === 'number' ? value : parseFloat(String(value)) || 0, 2);
    case 'boolean':
      return formatBooleanForExport(value as boolean);
    case 'percentage':
      return formatPercentageForExport(typeof value === 'number' ? value : parseFloat(String(value)) || 0);
    default:
      return String(value);
  }
}

/**
 * Converte colore hex a RGB
 */
function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
}

/**
 * Ottiene l'allineamento in base al tipo
 */
function getAlignment(type: ExportColumn['type']): 'left' | 'center' | 'right' {
  switch (type) {
    case 'currency':
    case 'number':
    case 'percentage':
      return 'right';
    case 'date':
      return 'center';
    default:
      return 'left';
  }
}

/**
 * Aggiunge header al documento PDF
 */
function addPdfHeader(doc: jsPDF, title: string, subtitle?: string): number {
  const { pdf, appName, colors } = EXPORT_CONFIG;
  const pageWidth = doc.internal.pageSize.getWidth();

  // Titolo applicazione
  doc.setFontSize(pdf.titleFontSize);
  doc.setTextColor(...hexToRgb(colors.primary));
  doc.setFont('helvetica', 'bold');
  doc.text(appName, pdf.marginLeft, pdf.marginTop);

  // Titolo report
  doc.setFontSize(pdf.subtitleFontSize);
  doc.setTextColor(...hexToRgb(colors.textPrimary));
  doc.text(title, pdf.marginLeft, pdf.marginTop + 7);

  // Data generazione
  doc.setFontSize(8);
  doc.setTextColor(...hexToRgb(colors.textSecondary));
  doc.setFont('helvetica', 'normal');
  const dateText = `Generato il: ${formatDateForExport(new Date())}`;
  doc.text(dateText, pageWidth - pdf.marginRight - doc.getTextWidth(dateText), pdf.marginTop);

  // Sottotitolo opzionale (es: periodo)
  let startY = pdf.marginTop + 15;
  if (subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(...hexToRgb(colors.textSecondary));
    doc.text(subtitle, pdf.marginLeft, startY);
    startY += 8;
  }

  return startY;
}

/**
 * Aggiunge footer con numero pagina
 */
function addPdfFooter(doc: jsPDF): void {
  const { pdf, appName } = EXPORT_CONFIG;
  const pageCount = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);

    // Numero pagina centrato
    const pageText = `Pagina ${i} di ${pageCount}`;
    doc.text(pageText, pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Nome app a sinistra
    doc.text(appName, pdf.marginLeft, pageHeight - 10);
  }
}

/**
 * Crea tabella con autoTable
 */
function createTable(
  doc: jsPDF,
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  startY: number,
  summaryData?: SummaryRow[]
): number {
  const { pdf, colors } = EXPORT_CONFIG;

  // Headers
  const headers = columns.map((col) => col.header);

  // Body data
  const body: RowInput[] = rows.map((row) =>
    columns.map((col) => {
      const value = getNestedValue(row, col.field);
      return {
        content: formatCellValueForPdf(value, col.type),
        styles: { halign: getAlignment(col.type) },
      } as CellInput;
    })
  );

  // Aggiungi righe di riepilogo
  if (summaryData && summaryData.length > 0) {
    // Il totale va nella colonna degli importi quando esiste: nell'ultima colonna,
    // spesso stretta, verrebbe spezzato su più righe e risulterebbe illeggibile.
    const currencyIndex = columns.findIndex((col) => col.type === 'currency');
    const summaryValueIndex = currencyIndex > 0 ? currencyIndex : columns.length - 1;

    // Riga vuota di separazione
    body.push(columns.map(() => ({ content: '', styles: { fillColor: [255, 255, 255] } })));

    summaryData.forEach((summary) => {
      const formattedValue =
        summary.type === 'currency' && typeof summary.value === 'number'
          ? formatCurrencyForExport(summary.value)
          : summary.type === 'number' && typeof summary.value === 'number'
          ? formatNumberForExport(summary.value, 0)
          : summary.type === 'percentage' && typeof summary.value === 'number'
          ? formatPercentageForExport(summary.value)
          : String(summary.value);

      // L'etichetta occupa tutte le colonne che precedono il valore: in una tabella
      // a molte colonne una singola cella la manderebbe a capo più volte.
      const summaryRow: CellInput[] = [
        {
          content: summary.label,
          colSpan: summaryValueIndex,
          styles: { fontStyle: 'bold', fillColor: hexToRgb(colors.summaryBg) },
        },
        {
          content: formattedValue,
          styles: {
            fontStyle: 'bold',
            halign: 'right',
            fillColor: hexToRgb(colors.summaryBg),
          },
        },
      ];

      for (let index = summaryValueIndex + 1; index < columns.length; index++) {
        summaryRow.push({ content: '', styles: { fillColor: hexToRgb(colors.summaryBg) } });
      }

      body.push(summaryRow);
    });
  }

  // Column widths
  const pageWidth = doc.internal.pageSize.getWidth();
  const tableWidth = pageWidth - pdf.marginLeft - pdf.marginRight;
  const totalDefinedWidth = columns.reduce((sum, col) => sum + (col.width || 15), 0);
  const columnStyles: Record<number, { cellWidth: number; halign: 'left' | 'center' | 'right' }> = {};

  columns.forEach((col, index) => {
    const proportion = (col.width || 15) / totalDefinedWidth;
    columnStyles[index] = {
      cellWidth: tableWidth * proportion,
      halign: getAlignment(col.type),
    };
  });

  // Font adattivo: con molte colonne lo spazio per cella si riduce e il testo andrebbe
  // a capo su troppe righe rendendo la tabella illeggibile. Riduciamo il corpo in modo
  // proporzionale alla larghezza media disponibile, senza toccare i report a poche colonne.
  const comfortableColumnWidth = 22; // mm: sopra questa soglia il font resta quello configurato
  const scale = Math.min(1, tableWidth / columns.length / comfortableColumnWidth);
  const bodyFontSize = Math.max(5.5, Math.round(pdf.fontSize * scale * 10) / 10);
  const headerFontSize = Math.max(5.5, Math.round(pdf.headerFontSize * scale * 10) / 10);

  autoTable(doc, {
    head: [headers],
    body: body,
    startY: startY,
    margin: { left: pdf.marginLeft, right: pdf.marginRight },
    styles: {
      fontSize: bodyFontSize,
      cellPadding: scale < 0.8 ? 1 : 2,
      overflow: 'linebreak',
      lineColor: hexToRgb(colors.borderColor),
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: hexToRgb(colors.headerBg),
      textColor: hexToRgb(colors.headerText),
      fontStyle: 'bold',
      halign: 'center',
      fontSize: headerFontSize,
    },
    alternateRowStyles: {
      fillColor: hexToRgb(colors.altRowBg),
    },
    columnStyles: columnStyles,
    didDrawPage: () => {
      // Hook per aggiungere header su ogni pagina se necessario
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (doc as any).lastAutoTable?.finalY || startY + 20;
}

/**
 * Export singola tabella PDF
 */
export async function exportToPdf(
  config: ExportConfig,
  data: Record<string, unknown>[],
  subtitle?: string
): Promise<void> {
  if (data.length === 0) {
    throw new Error('Nessun dato da esportare');
  }

  const orientation = config.orientation || 'portrait';

  const doc = new jsPDF({
    orientation: orientation,
    unit: 'mm',
    format: 'a4',
  });

  // Header
  const startY = addPdfHeader(doc, config.title, subtitle);

  // Tabella
  createTable(doc, config.columns, data, startY, config.summaryData);

  // Footer
  addPdfFooter(doc);

  // Download
  const filename = generateFilename(config.filename, 'pdf');
  doc.save(filename);
}

/**
 * Export multi-sezione PDF
 */
export async function exportToPdfMultiSection(
  config: MultiSheetExportConfig,
  subtitle?: string
): Promise<void> {
  const { pdf } = EXPORT_CONFIG;

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  let currentY = addPdfHeader(doc, config.title, subtitle);
  let isFirstSection = true;

  for (const section of config.sheets) {
    if (section.rows.length === 0) continue;

    // Nuova pagina per sezioni successive (tranne la prima)
    if (!isFirstSection) {
      doc.addPage();
      currentY = addPdfHeader(doc, config.title, subtitle);
    }

    // Titolo sezione
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(section.title, pdf.marginLeft, currentY);
    currentY += 7;

    // Tabella sezione
    currentY = createTable(doc, section.columns, section.rows, currentY, section.summaryData);
    currentY += 10;

    isFirstSection = false;
  }

  // Footer
  addPdfFooter(doc);

  // Download
  const filename = generateFilename(config.filename, 'pdf');
  doc.save(filename);
}
